import { Outlet } from 'react-router';
import { useSession } from '../features/session/session';
import { EntreeDeDeveloppement } from '../features/session/EntreeDeDeveloppement';
import { Squelette } from '../shared/ui/Squelette';
import { Bandeau } from '../shared/ui/Bandeau';
import { useSessionTombee } from './Fournisseurs';

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * **CE QUI SE PASSE QUAND PERSONNE N'EST CONNECTÉ — ET `web/` N'A PAS D'ÉCRAN
 * DE CONNEXION.**
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * `CDC-6` F-71 tranche : « **l'écran de connexion est une page du site vitrine**, et son
 * contenu ne se rejoue pas », page 6 de la liste fermée des huit. `CDC-5` le confirme de
 * l'autre côté — la surface d'administration « s'atteint par **la page de connexion unique
 * du site vitrine**, celle des quatre rôles ». Et « **se déconnecter ramène sur la page de
 * connexion, pas sur l'accueil du site** ».
 *
 * **`web/` ne porte donc AUCUN formulaire de connexion en production.** En poser un ferait
 * un second écran de connexion, sur un second domaine, pour un produit dont le corpus dit
 * qu'il n'en a qu'un.
 *
 * ───────────────────────────────────────────────────────────────────────────
 * **CE QUE CETTE RÈGLE COÛTE, ET IL FAUT LE LIRE AVANT DE DÉPLOYER.**
 * ───────────────────────────────────────────────────────────────────────────
 *
 * **`web/` n'est atteignable que par deux chemins**, et l'un des deux n'existe pas
 * encore :
 *
 * 1. **le lien d'auto-connexion d'un cours**, qui « va **DROIT à l'application** : le site
 *    vitrine n'est jamais traversé » (F-71). Il existe, `POST /api/v1/acces/{valeur}` ;
 * 2. **la page de connexion de `site/`**, qui appartient au **LOT-6**, dernier du chantier.
 *
 * **Les deux chemins se mordent la queue** : pour recevoir un lien d'auto-connexion il
 * faut qu'un tuteur ait créé un cours, et pour créer un cours il faut être entré dans
 * l'application. **Tant que `site/` n'existe pas, l'application ne s'ouvre à personne.**
 * C'est une contrainte d'ordre que le tableau des vagues n'avait pas vue — il classe
 * `CDC-6` en dernier parce qu'il dépend de `CDC-5` **pour son carrousel**, pas pour sa page
 * de connexion.
 *
 * **L'ENTRÉE DE DÉVELOPPEMENT EXISTE POUR CELA, ET ELLE NE PART PAS EN PRODUCTION.**
 * `import.meta.env.DEV` est remplacé par `false` à la construction, et le corps du bloc
 * disparaît du paquet : ce n'est pas une garde d'exécution qu'on pourrait contourner,
 * c'est du code **absent** de l'artefact déployé.
 */
export function PortailDeSession() {
  const session = useSession();
  const { sessionTombee } = useSessionTombee();

  if (session.isLoading) {
    return (
      <main className="mx-auto flex max-w-etroite flex-col gap-4 p-8">
        <Squelette lignes={4} />
      </main>
    );
  }

  if (session.data === undefined) {
    if (import.meta.env.DEV) {
      return <EntreeDeDeveloppement sessionTombee={sessionTombee} />;
    }
    // **La destination, et jamais une valeur qui vaut accès** — F-71 : « à travers cette
    // page, ce qui est retenu est une DESTINATION, jamais une valeur qui vaut accès.
    // Sans cette borne, la page de connexion deviendrait un endroit où une valeur
    // d'accès attend, **sur le domaine public**. »
    const adresse = adresseDeConnexion();
    if (adresse === null) {
      // **Sans `VITE_SITE_URL`, on ne redirige nulle part : on nomme la variable.** Un écran
      // laid qui dit quoi faire vaut mieux qu'un écran qui boucle — et c'est la leçon que
      // `admin/` a payée en recette le 20260824.
      return (
        <main className="mx-auto flex max-w-etroite flex-col gap-4 p-8">
          <Bandeau ton="echec">
            Cette surface est mal configurée : la variable de construction VITE_SITE_URL est
            absente. Elle porte l’adresse du site vitrine, où vit la page de connexion — cette
            application n’en a aucune. C’est une variable de BUILD : la poser demande une
            reconstruction, jamais un redémarrage.
          </Bandeau>
        </main>
      );
    }
    window.location.replace(adresse);
    return null;
  }

  return <Outlet />;
}

/**
 * L'adresse de la page de connexion, chez `site/`.
 *
 * **`SITE_URL` remonte en catégorie 1 au lot qui crée `web/` ou `site/`** (D-022), et
 * c'est ce lot-ci : elle n'est plus « déclarée mais lue par aucun code ».
 */
function adresseDeConnexion(): string | null {
  const site = import.meta.env.VITE_SITE_URL;
  // **RIEN, ET SURTOUT PAS `'/'` — UN REPLI À LA RACINE EST UNE BOUCLE INFINIE.**
  //
  // Ce repli valait `'/'`. Constaté sur `admin/` en recette le 20260824, où la même ligne a
  // produit une panne complète : l'application déployée **sans aucune variable de build**,
  // la session illisible faute d'API, le renvoi vers `/`, la session encore illisible — **et
  // le navigateur qui recharge sans fin**, sans une erreur, sans un message, sans rien qui
  // nomme la variable manquante.
  //
  // `web/` portait le même défaut, **latent** : personne ne s'était encore connecté à sa
  // copie de recette. `null` oblige l'appelant à traiter le cas, et le compilateur l'y force.
  if (site === undefined || site === '') return null;
  return `${site.replace(/\/$/, '')}/connexion`;
}
